import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlList } from 'app/main/pages/authentication/shared/url-list.enum';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { RequestBody } from '../detail/config/request-body';
import { drawflowDefault } from './utils';
import { LocalStorageService } from 'app/services/local-storage.service';
import { BehaviorSubject } from 'rxjs';
import { NameAvailabilityResponse } from '../detail/config/name-availability-response';

@Injectable()
export class ConversationFlowService {
  private flowUrl = `${UrlList.URL_MESSAGE_CHAT_BOT}/conversation-flows`;
  private urlContacts = UrlList.URL_CONTACTS;
  private urlRooms = UrlList.URL_ROOMS;
  private urlTemplates = UrlList.URL_TEMPLATES;

  // Guardamos la página completa, no solo el contenido
  roomPage: Page<any> | null = null;
  public allRooms: any[] = [];
  isLoadingRoom = false;
  errorRoom: string | null = null;

  // Estado de la paginación
  currentPageRoom: number = 0;
  pageSizeRoom: number = 10;
  totalPagesRoom = 1;
  WHATSSAP_FLOW_PERMITIONS = false;

  readonly PAGE_SIZE_ROOM = 40;

  labels: any;
  onShowPreLoading: BehaviorSubject<boolean>;
  private hasBlockNavigation: boolean = false;

  constructor(
    private _httpClient: HttpClient,
    private _localStorageService: LocalStorageService
  ) {
    this.onShowPreLoading = new BehaviorSubject(false);
  }

  getIsNameAvailable(nameFlow: string, flowId?: number): Observable<any> {
    const customerId = this._localStorageService.getCurrentUser().customerId;
    let url = `${UrlList.URL_MESSAGE_CHAT_BOT}/conversation/v1/customer/${customerId}/name-flow/${nameFlow}`;

    if (flowId) {
      url += `?flowId=${flowId}`;
    }

    return this._httpClient.get<NameAvailabilityResponse>(url);
  }

  createConversationFlow(body: RequestBody): Observable<any> {
    return this._httpClient.post(this.flowUrl, body);
  }

  updateConversationFlow(body: RequestBody): Observable<any> {
    return this._httpClient.put(this.flowUrl, body);
  }

  getConversationFlow(conversationFlowId: number): Observable<any> {
    const url = `${this.flowUrl}/${conversationFlowId}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._httpClient.get(url, { headers: headers });
  }

  getLabels(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._httpClient
        .get(
          this.urlContacts +
            UrlList.CUSTOMER_LABELS +
            this._localStorageService.getCurrentUser().customerId
        )
        .subscribe((response: any) => {
          this.labels = response.sort((a, b) => {
            const nameA = a.label.name.toLowerCase();
            const nameB = b.label.name.toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          });
          resolve(this.labels);
        }, reject);
    });
  }

  getDrawflowConfigWelcome(): Observable<any> {
    return of({
      drawflow: {
        Home: {
          data: {
            '1': {
              id: 1,
              name: 'welcome',
              data: {},
              class: 'welcome',
              html: '\n <div>\n <div class="header-container">👏 tutorial</div>\n <div class="box">\n <div class="box-container">\n <p>Comienza a diseñar tu <b>flujo conversacional</b> personalizando cada detalle.\n <p><b>Elementos disponibles:</b>\n <p>Flujos <br>\ Menús<br>\n Opciones<br>\n Imágenes<br>\n y más.<br>\n </p>\n <br>\n <p><b><u>Atajos:</u></b></p>\n 💠 Click primario == Mover<br>\n ❌ Click secundario == Eliminar<br>\n 🔍 Ctrl + Rueda == Zoom<br>\n ...</p>\n </div>\n </div>\n </div>\n ',
              typenode: false,
              inputs: {},
              outputs: {},
              pos_x: 50,
              pos_y: 50,
            },
          },
        },
      },
    }).pipe(delay(1000));
  }

  /**
   * Method that returns a default flow, displaying the FLUJO, PRESENTACIÓN, DATOS REQUERIDOS,
   * and MENU PRINCIPAL components once loaded in the view.
   *
   * @returns An observable that emits the default flow.
   */
  getDrawflowDefault(): Observable<any> {
    // The method returns a JSON object that represents the default flow
    return of(drawflowDefault).pipe(delay(1000));
  }

  setBlockNavigation(isBlocked: boolean) {
    this.hasBlockNavigation = isBlocked;
  }

  getBlockNavigation() {
    return this.hasBlockNavigation;
  }

  validateKeywordsOnEditRoom(
    flowId: number,
    keywordList: string[],
    roomId?: number
  ) {
    const body = {
      flowId,
      keywordList,
      roomId,
    };
    return this._httpClient.post(
      `${this.urlRooms}/room/v1/validate-keywords-on-edit-flow`,
      body,
      { responseType: 'text' }
    );
  }

  validateConversationFlowAssignment(body: any): Observable<any> {
    return this._httpClient.post(
      `${this.urlRooms}/room/v1/validate-conversation-flow-assignment`,
      body
    );
  }

  getRoomsCustomer(
    channelId: number = 1,
    page: number = 0,
    size: number = 10
  ): Observable<any> {
    const customerUserId = this._localStorageService.getCurrentUser().id;

    let params = new HttpParams()
      .set('customerUserId', customerUserId.toString())
      .set('channelId', channelId.toString())
      .set('page', page.toString())
      .set('size', size.toString());

    return this._httpClient.get<any>(`${this.urlRooms}/room/v1/by-customer-user`, {
      params,
    });
  }

  /**
   * Checks if a specific setting is enabled for the current user.
   * @param {string} featureName - The exact name of the setting to search for.
   * @returns {boolean} - Returns 'true' if the setting exists, 'false' otherwise.
   */
  isFeatureEnabled(featureName) {
    const userProfile = this._localStorageService.getCurrentUser();
    // Primero, nos aseguramos de que las configuraciones existan para evitar errores
    if (
      userProfile &&
      userProfile.frontConfigurations &&
      userProfile.frontConfigurations.configurations
    ) {
      const configuraciones = userProfile.frontConfigurations.configurations;

      // El método .includes() devuelve true si el array contiene el elemento, y false si no.
      return configuraciones.includes(featureName);
    }
    // Si no hay configuraciones, devolvemos 'false' por seguridad.
    return false;
  }
}

// En un archivo (ej. page.model.ts)
export interface Page<T> {
  content: T[];
  pageable: {
    sort: {
      sorted: boolean;
      unsorted: boolean;
      empty: boolean;
    };
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

interface Label {
  id: number;
  name: string;
  color: string | null;
  description: string;
  icon: string;
  config: string;
  state: boolean;
}

interface LabelResponse {
  id: number;
  customersId: number;
  label: Label;
}
